﻿import { request, EMISSION_BASE, ORGANISATION_BASE } from "@/api/client";
import { uploadFile as uploadFileToBucket } from "@/api/upload";
import type {
  CompanyData,
  UpdateSettingsPayload,
  CompanyPreferences,
  CurrencyOption,
  CreateExchangeRatePayload,
  ExchangeRatePeriod,
} from "../types/settings";

// --- Company ---

export function fetchCompany(): Promise<CompanyData> {
  return request("get", `${ORGANISATION_BASE}/company`, {
    timeout: 30_000,
    unwrap: false,
  });
}

export function updateCompany(payload: UpdateSettingsPayload): Promise<void> {
  const body = {
    id: payload.id,
    description: payload.description,
    logo: payload.logo,
    organisationBoundary: payload.organisation_boundary,
    address: {
      addressLine1: payload.address_line1,
      city: payload.city,
      cityCode: payload.city_code,
      state: payload.state,
      stateCode: payload.state_code,
      country: payload.country,
      countryCode: payload.country_code,
      pinCode: payload.pin_code,
    },
  };
  return request("put", `${ORGANISATION_BASE}/settings-info`, {
    data: body,
    timeout: 30_000,
    unwrap: false,
  });
}

// --- Preferences ---

interface RawPreferencesResponse {
  id?: number | string;
  email_auth_enabled?: boolean;
  app_auth_enabled?: boolean;
  ai_assistant_enabled?: boolean;
  companyPreference: Record<string, unknown>;
  reports?: Array<{ id: number; name: string }>;
  [key: string]: unknown;
}

export async function fetchPreferences(fetchReports = false): Promise<{
  companyPreference: CompanyPreferences;
  reports?: Array<{ id: number; name: string }>;
  emailAuthEnabled?: boolean;
  appAuthEnabled?: boolean;
  aiAssistantEnabled?: boolean;
}> {
  const params = fetchReports ? { fetchReports: "" } : undefined;
  const raw = await request<RawPreferencesResponse>(
    "get",
    `${ORGANISATION_BASE}/companypreferences`,
    {
      params,
      timeout: 30_000,
      unwrap: false,
    },
  );
  const cp = (raw.companyPreference ?? raw) as Record<string, unknown>;
  let reports: { framework: number[]; management: number[] } | undefined;
  if (typeof cp.reports === "string") {
    try {
      reports = JSON.parse(cp.reports);
    } catch {
      reports = undefined;
    }
  } else if (cp.reports && typeof cp.reports === "object") {
    reports = cp.reports as { framework: number[]; management: number[] };
  }
  const emailAuthEnabled =
    (raw.email_auth_enabled as boolean | undefined) ??
    (cp.emailAuthEnabled as boolean | undefined) ??
    (cp.email_auth_enabled as boolean | undefined);
  const appAuthEnabled =
    (raw.app_auth_enabled as boolean | undefined) ??
    (cp.appAuthEnabled as boolean | undefined) ??
    (cp.app_auth_enabled as boolean | undefined);
  const aiAssistantEnabled =
    (raw.ai_assistant_enabled as boolean | undefined) ??
    (cp.aiAssistantEnabled as boolean | undefined) ??
    (cp.ai_assistant_enabled as boolean | undefined);
  return {
    companyPreference: {
      ...(cp as Omit<CompanyPreferences, "reports">),
      timeZone:
        typeof (cp.timeZone ?? cp.time_zone) === "string"
          ? String(cp.timeZone ?? cp.time_zone)
          : "UTC",
      allocationFacility: (cp.allocationFacility ?? cp.allocation_facility) as
        number | string | undefined,
      reports,
      emailAuthEnabled,
      appAuthEnabled,
      aiAssistantEnabled,
    },
    reports: raw.reports,
    emailAuthEnabled,
    appAuthEnabled,
    aiAssistantEnabled,
  };
}

export function updatePreferences(
  payload: Partial<CompanyPreferences>,
): Promise<void> {
  const body: Record<string, unknown> = { id: payload.id };
  if (payload.timeZone !== undefined) body.timeZone = payload.timeZone;
  if (payload.is_calendar_year !== undefined)
    body.isCalendarYear = payload.is_calendar_year;
  if (payload.financial_year !== undefined)
    body.financialYear = payload.financial_year;
  if (payload.reports !== undefined) body.reports = payload.reports;
  if (payload.currency !== undefined) body.currency = payload.currency;
  if (payload.gwpVersion !== undefined) body.gwpVersion = payload.gwpVersion;
  if (payload.companyUrl !== undefined) body.companyUrl = payload.companyUrl;
  if (payload.allocationFacility !== undefined)
    body.allocationFacility = payload.allocationFacility;
  if (payload.emailAuthEnabled !== undefined)
    body.emailAuthEnabled = payload.emailAuthEnabled;
  if (payload.appAuthEnabled !== undefined)
    body.appAuthEnabled = payload.appAuthEnabled;
  if (payload.privacyPolicy !== undefined)
    body.privacyPolicy = payload.privacyPolicy;
  if (payload.termsAndConditions !== undefined)
    body.termsAndConditions = payload.termsAndConditions;
  if (payload.aiAssistantEnabled !== undefined)
    body.aiAssistantEnabled = payload.aiAssistantEnabled;
  return request("put", `${ORGANISATION_BASE}/companypreferences`, {
    data: body,
    timeout: 30_000,
    unwrap: false,
  });
}

// --- Currencies ---

export function fetchCurrencies(): Promise<CurrencyOption[]> {
  return request("get", `${EMISSION_BASE}/alluoms`, {
    params: { type: "Currency" },
    timeout: 30_000,
    unwrap: false,
  });
}

// --- Exchange Rates ---

export function fetchExchangeRates(): Promise<ExchangeRatePeriod[]> {
  return request("get", `${EMISSION_BASE}/currency-conversions`, {
    timeout: 30_000,
    unwrap: false,
  });
}

export function createExchangeRates(
  payload: CreateExchangeRatePayload,
): Promise<void> {
  const body = { ...payload, period: payload.periodType };
  delete (body as Record<string, unknown>).periodType;
  return request("post", `${EMISSION_BASE}/currency-conversions`, {
    data: body,
    timeout: 30_000,
    unwrap: false,
  });
}

// --- Upload ---

export async function uploadFile(file: File): Promise<string> {
  const body = await uploadFileToBucket(file, "images");
  return body.Location;
}
